import React, { useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Alert, Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || '';

const fmtDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

/**
 * Lets the patient flag — before or after accepting an estimate — that
 * they'd like staff to revise it. Works the same in both contexts; the
 * backend records whichever estimate (if any) is selected plus a note, and
 * it shows up for staff as a status chip on the Estimate Builder.
 *
 * Props:
 *  - token: share token
 *  - estimateOptions: [{ estimate_id, variation_label }] — when provided (and
 *    has >1 entry), the patient can pick which estimate the request is about,
 *    plus a "something different" option. Omit/empty for a single fixed target.
 *  - fixedEstimate: { estimate_id, variation_label } — used when there's no
 *    picker (e.g. the already-accepted estimate in the Surgery Portal).
 *  - existingRequestAt: ISO date string if a request is already pending —
 *    shown as an inline acknowledgment instead of hiding the button.
 *  - onSubmitted: called after a successful submit (e.g. to refresh status).
 *  - variant/color/size/label: button styling overrides.
 */
export default function AmendmentRequestButton({
  token, estimateOptions = [], fixedEstimate = null, existingRequestAt = null,
  onSubmitted, variant = 'outlined', color = 'inherit', size = 'medium', label,
}) {
  const [open, setOpen] = useState(false);
  const [selectedEstimateId, setSelectedEstimateId] = useState(fixedEstimate?.estimate_id ?? '');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const hasPicker = estimateOptions.length > 1;

  const handleOpen = () => {
    setSelectedEstimateId(fixedEstimate?.estimate_id ?? '');
    setNote('');
    setError(null);
    setJustSubmitted(false);
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!note.trim()) { setError('Please describe what you\u2019d like changed.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/estimates/share/${token}/request-amendment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estimateId: hasPicker ? (selectedEstimateId || null) : (fixedEstimate?.estimate_id || null),
          note: note.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to submit request'); return; }
      setJustSubmitted(true);
      onSubmitted?.();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        color={color}
        size={size}
        startIcon={<EditNoteIcon fontSize="small" />}
        onClick={handleOpen}
      >
        {label || 'Request a Change'}
      </Button>

      {existingRequestAt && !open && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          A request was sent on {fmtDate(existingRequestAt)} — our team will follow up.
        </Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request an Amendment</DialogTitle>
        <DialogContent>
          {justSubmitted ? (
            <Alert severity="success">
              Thanks — your request has been sent to our team. They'll follow up with updated details.
            </Alert>
          ) : (
            <>
              {existingRequestAt && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You already have a pending request from {fmtDate(existingRequestAt)}. Submitting again will update it.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Let us know what you'd like changed and our team will follow up with an updated estimate.
              </Typography>
              {hasPicker && (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Which estimate is this about?</InputLabel>
                  <Select
                    value={selectedEstimateId}
                    label="Which estimate is this about?"
                    onChange={(e) => setSelectedEstimateId(e.target.value)}
                  >
                    <MenuItem value="">Something different / not sure</MenuItem>
                    {estimateOptions.map(est => (
                      <MenuItem key={est.estimate_id} value={est.estimate_id}>{est.variation_label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {!hasPicker && fixedEstimate && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Regarding: <strong>{fixedEstimate.variation_label}</strong>
                </Typography>
              )}
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={3}
                label="What would you like changed?"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {justSubmitted ? (
            <Button onClick={() => setOpen(false)}>Close</Button>
          ) : (
            <>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button variant="contained" disabled={submitting} onClick={handleSubmit}>
                {submitting ? 'Sending…' : 'Send Request'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
